import { useEffect, useMemo, useState } from "react";
import { useTasks } from "~/apis/use-tasks";
import { useActionMutations } from "~/pages/administration/workflows-page/hooks/apis/use-action-mutations";
import type { Item } from "~/types/api";
import { ChangeStatusDropdown } from "./change-status-dropdown";
import { useUpdateItem } from "../../../hooks/apis/use-update-item-status";
import type { StatusGroup } from "../../../task.types";

type Props = {
  sortedItems: StatusGroup[];
  selectedExplorerItems: Set<number>;
  taskIds?: number[] | null;
  onClose: () => void;
};

export const ChangeStatusPanel = ({
  sortedItems,
  selectedExplorerItems,
  taskIds,
  onClose,
}: Props) => {
  const [selectedStatusCode, setSelectedStatusCode] = useState<number | null>(null);
  const { updateItems, isSubmitting } = useUpdateItem();
  const uniqueWorkflowIds = useMemo(
    () =>
      Array.from(
        new Set(
          sortedItems.flatMap((group) =>
            group.children
              .map((item) => item.workflowId)
              .filter((workflowId): workflowId is number => workflowId !== undefined)
          )
        )
      ),
    [sortedItems]
  );
  const { tasks, isLoading: isLoadingTasks } = useTasks();
  const { actions } = useActionMutations(uniqueWorkflowIds);

  const actionStatusCodes = useMemo(
    () => new Set(actions.flatMap((a) => (a.statuses ?? []).map((s) => s.statusCode))),
    [actions]
  );

  const allItems: Item[] = sortedItems.flatMap((group) => group.children);

  const taskWorkflowIds = useMemo(() => {
    const effectiveTaskIds =
      taskIds && taskIds.length > 0
        ? taskIds
        : [
            ...new Set(
              allItems.map((item) => item.taskId).filter((id): id is number => id !== undefined)
            ),
          ];

    return effectiveTaskIds
      .map((id) => tasks.find((t) => t.id === id)?.workflowId)
      .filter((wId): wId is number => wId !== undefined)
      .filter((wId, i, arr) => arr.indexOf(wId) === i);
  }, [allItems, taskIds, tasks]);

  const handleSubmit = async (statusCode: number) => {
    if (isSubmitting) return;
    if (selectedExplorerItems.size === 0) return;
    const success = await updateItems(allItems, selectedExplorerItems, statusCode);
    if (success) {
      onClose();
    }
  };

  const handleStatusChange = (statusCode: number) => {
    setSelectedStatusCode(statusCode);
    handleSubmit(statusCode);
  };

  useEffect(() => {
    if (selectedExplorerItems.size != 0) {
      const lowestStatusCode = Math.min(
        ...allItems
          .filter((item) => selectedExplorerItems.has(item.id))
          .map((item) => item.statusCode)
          .filter((code): code is number => code !== undefined)
      );
      setSelectedStatusCode(lowestStatusCode);
    }
  }, [selectedExplorerItems, allItems]);

  if (isLoadingTasks) {
    return (
      <div className="border-gxe-border border-b bg-gray-50 p-4">
        <div className="flex h-10 w-full items-center justify-center rounded-md border border-gray-300 bg-white">
          <span className="text-sm text-gray-500">Loading statuses...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="border-gxe-border border-b bg-gray-50 p-4">
      <ChangeStatusDropdown
        workflowIds={taskWorkflowIds}
        selectedStatusCode={selectedStatusCode}
        onStatusChange={handleStatusChange}
        actionStatusCodes={actionStatusCodes}
      />
    </div>
  );
};
